import React, { useState, useEffect, useRef, TouchEvent } from 'react';
import {
  CarouselContainer,
  Indicator,
  Indicators,
  Slide,
  SlideContainer,
  SlideContent,
  SlideDescription,
  SlideImage,
  Slides,
  SlideTitle,
  TextContainer,
} from './Carousel.styles';

export interface CarouselProps {
  slides: {
    id: number;
    image: string;
    title: string;
    description: string;
  }[];
  autoplayInterval?: number;
  slideAriaLabel: string;
}

const Carousel: React.FC<CarouselProps> = ({
  slides,
  autoplayInterval = 5000,
  slideAriaLabel,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isTouching, setIsTouching] = useState(false);

  const autoplayTimerRef = useRef<NodeJS.Timeout | null>(null);
  const slideContainerRef = useRef<HTMLDivElement>(null);

  // Handle autoplay
  useEffect(() => {
    startAutoplay();

    return () => {
      if (autoplayTimerRef.current) {
        clearInterval(autoplayTimerRef.current);
      }
    };
  }, [currentSlide]);

  const startAutoplay = () => {
    if (autoplayTimerRef.current) {
      clearInterval(autoplayTimerRef.current);
    }

    autoplayTimerRef.current = setInterval(() => {
      nextSlide();
    }, autoplayInterval);
  };

  const pauseAutoplay = () => {
    if (autoplayTimerRef.current) {
      clearInterval(autoplayTimerRef.current);
      autoplayTimerRef.current = null;
    }
  };

  const nextSlide = () => {
    setCurrentSlide((prevSlide) =>
      prevSlide === slides.length - 1 ? 0 : prevSlide + 1
    );
  };

  const prevSlide = () => {
    setCurrentSlide((prevSlide) =>
      prevSlide === 0 ? slides.length - 1 : prevSlide - 1
    );
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  // Touch event handlers for manual sliding
  const handleTouchStart = (e: TouchEvent) => {
    setIsTouching(true);
    pauseAutoplay();
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (isTouching) {
      setTouchEnd(e.targetTouches[0].clientX);
    }
  };

  const handleTouchEnd = () => {
    setIsTouching(false);
    startAutoplay();

    // If swipe distance is significant enough (more than 50px)
    if (touchStart - touchEnd > 50) {
      // Swipe left - go to next slide
      nextSlide();
    } else if (touchEnd - touchStart > 50) {
      // Swipe right - go to previous slide
      prevSlide();
    }
  };

  return (
    <CarouselContainer>
      <SlideContainer
        ref={slideContainerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <Slides $currentSlide={currentSlide}>
          {slides.map((slide) => (
            <Slide key={slide.id}>
              <SlideContent>
                <SlideImage
                  src={slide.image}
                  alt={slide.title}
                />
                <TextContainer>
                  <SlideTitle>{slide.title}</SlideTitle>
                  <SlideDescription>{slide.description}</SlideDescription>
                </TextContainer>
              </SlideContent>
            </Slide>
          ))}
        </Slides>
      </SlideContainer>

      <Indicators>
        {slides.map((_, index) => (
          <Indicator
            key={index}
            $active={currentSlide === index}
            onClick={() => goToSlide(index)}
            aria-label={`${slideAriaLabel} ${index + 1}`}
          />
        ))}
      </Indicators>
    </CarouselContainer>
  );
};

export default Carousel;
